/**
 * Selector en cascada Campaña → Aviso (obligatorio) → Placement (opcional)
 * para vincular un anexo o indicador de reporte a un aviso/placement concreto.
 * El portal agrupa la evidencia por aviso.
 */
import { useQuery } from "@tanstack/react-query";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { media, type ReportTarget } from "@/lib/api";

export function ReportTargetPicker({
  value, onChange, requireAd = true, clientId,
}: {
  value: ReportTarget;
  onChange: (t: ReportTarget) => void;
  requireAd?: boolean;
  clientId?: number | null;
}) {
  const campaignsQ = useQuery({
    queryKey: ["media", "campaigns", "picker", clientId ?? null],
    queryFn: () => media.listCampaigns({ client_id: clientId ?? undefined, limit: 300 }),
  });
  const adsQ = useQuery({
    queryKey: ["media", "ads", "by_campaign", value.campaign_id ?? null],
    queryFn: () => media.listAds({ campaign_id: value.campaign_id!, limit: 500 }),
    enabled: !!value.campaign_id,
  });
  const placementsQ = useQuery({
    queryKey: ["media", "placements", "by_ad", value.ad_id ?? null],
    queryFn: () => media.listPlacements({ ad_id: value.ad_id!, limit: 200 }),
    enabled: !!value.ad_id,
  });
  const outletsQ = useQuery({
    queryKey: ["media", "outlets", "all"],
    queryFn: () => media.listOutlets({ limit: 500 }),
    enabled: !!value.ad_id,
  });
  const outletName = (id: number) => (outletsQ.data ?? []).find(o => o.id === id)?.name ?? `Medio #${id}`;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 rounded-lg bg-ink-50/60 dark:bg-ink-800/40 border border-ink-200 dark:border-ink-700">
      <div>
        <Label className="text-xs">Campaña</Label>
        <Select
          value={value.campaign_id ? String(value.campaign_id) : ""}
          onValueChange={(v) => onChange({ campaign_id: Number(v), ad_id: undefined, placement_id: undefined })}
        >
          <SelectTrigger><SelectValue placeholder="Seleccionar…" /></SelectTrigger>
          <SelectContent>
            {(campaignsQ.data ?? []).map(c => (
              <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-xs">Aviso {requireAd && <span className="text-bad">*</span>}</Label>
        <Select
          value={value.ad_id ? String(value.ad_id) : ""}
          onValueChange={(v) => onChange({ ...value, ad_id: Number(v), placement_id: undefined })}
          disabled={!value.campaign_id}
        >
          <SelectTrigger><SelectValue placeholder={value.campaign_id ? "Seleccionar…" : "Elige campaña"} /></SelectTrigger>
          <SelectContent>
            {(adsQ.data ?? []).map(a => (
              <SelectItem key={a.id} value={String(a.id)}>
                {a.code} — {a.title}
              </SelectItem>
            ))}
            {!!value.campaign_id && !(adsQ.data ?? []).length && !adsQ.isLoading && (
              <div className="px-2 py-2 text-xs text-ink-400">Sin avisos en la campaña</div>
            )}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-xs">Placement (opcional)</Label>
        <Select
          value={value.placement_id ? String(value.placement_id) : "none"}
          onValueChange={(v) => onChange({ ...value, placement_id: v === "none" ? undefined : Number(v) })}
          disabled={!value.ad_id}
        >
          <SelectTrigger><SelectValue placeholder={value.ad_id ? "Todos" : "Elige aviso"} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">— Todo el aviso</SelectItem>
            {(placementsQ.data ?? []).map(p => (
              <SelectItem key={p.id} value={String(p.id)}>
                {outletName(p.outlet_id)}{p.scheduled_at ? ` · ${p.scheduled_at}` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
